import { useState, useEffect, useRef } from "react";
import styles from "./styles.module.scss";
import { MetricKeys, metricsMock, mockChartData } from "./mock";
import classNames from "classnames";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts";
import { Checkbox } from "antd";

export const ChartOrganic = () => {
  const defaultMetrics = [
    MetricKeys.CLICK_THROUGHS,
    MetricKeys.TOTAL_DPV,
    MetricKeys.TOTAL_ATC,
    MetricKeys.PURCHASES,
    MetricKeys.UNITS_SOLD,
    MetricKeys.BRAND_REFERRAL_BONUS,
  ];

  const [selectedMetrics, setSelectedMetrics] = useState(defaultMetrics);
  const [dropdownIndex, setDropdownIndex] = useState<number | null>(null);
  const [activeSelect, setActiveSelect] = useState<number[]>([
    0,
    defaultMetrics.length - 1,
  ]);

  const dropdownRef = useRef<HTMLDivElement>(null);
  const activeMetrics = activeSelect.map((index) => selectedMetrics[index]);

  const handleDropdownToggle = (index: number) => {
    setDropdownIndex(dropdownIndex === index ? null : index);
  };

  const handleMetricChange = (index: number, newMetric: MetricKeys) => {
    const updatedMetrics = [...selectedMetrics];
    updatedMetrics[index] = newMetric;
    setSelectedMetrics(updatedMetrics);
    setDropdownIndex(null);
  };

  const handleSelectToggle = (index: number) => {
    setActiveSelect((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index],
    );
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setDropdownIndex(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div className={styles.chartWrapper}>
      <div className={styles.chartHeader}>
        <h2>Organic sales attribution</h2>
      </div>
      <div className={styles.chartSelects}>
        {selectedMetrics.map((metricKey, index) => {
          const metric = metricsMock.find((m) => m.key === metricKey);
          const isActive = activeSelect.includes(index);

          return (
            <div
              ref={dropdownIndex === index ? dropdownRef : null}
              className={classNames(
                styles.selectBox,
                isActive && styles[metric?.colorName || ""],
              )}
              key={metricKey}
            >
              <div className={styles.selected}>
                <div
                  className={styles.trigger}
                  onClick={() => handleSelectToggle(index)}
                >
                  {isActive ? (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                    >
                      <path
                        d="M2 10L7.33333 15L18 5"
                        stroke="#5295E0"
                        strokeWidth="2.4"
                      />
                    </svg>
                  ) : (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                    >
                      <rect
                        x="1"
                        y="1"
                        width="18"
                        height="18"
                        rx="3"
                        stroke="#2E2E2E"
                        strokeWidth="2"
                      />
                    </svg>
                  )}
                </div>
                <div className={styles.selectedLabel}>{metric?.label}</div>
                <span className={styles.value}>
                  {metric?.key === MetricKeys.BRAND_REFERRAL_BONUS
                    ? `$${metric?.value.toLocaleString()}`
                    : metric?.value.toLocaleString()}
                </span>
              </div>
              <div
                className={styles.dropdown}
                onClick={() => handleDropdownToggle(index)}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M2.76534 6.01118C2.33691 5.56195 2.33691 4.85541 2.76534 4.40617L2.78809 4.38232C3.24623 3.90194 4.01289 3.90194 4.47103 4.38232L7.15853 7.20032C7.61667 7.6807 8.38333 7.6807 8.84147 7.20032L11.529 4.38232C11.9871 3.90194 12.7538 3.90194 13.2119 4.38233L13.2347 4.40617C13.6631 4.85541 13.6631 5.56195 13.2347 6.01118L8.84147 10.6177C8.38333 11.0981 7.61667 11.0981 7.15853 10.6177L2.76534 6.01118Z"
                    fill="white"
                  />
                </svg>
              </div>

              {dropdownIndex === index && (
                <div className={styles.dropdownMenu}>
                  {metricsMock.map((m) => (
                    <div
                      key={m.key}
                      className={styles.dropdownItem}
                      onClick={() => handleMetricChange(index, m.key)}
                    >
                      <Checkbox
                        checked={selectedMetrics.includes(m.key)}
                        onChange={() => handleMetricChange(index, m.key)}
                      >
                        <span className={styles.checkboxLabel}>{m.label}</span>
                      </Checkbox>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div style={{ width: "100%", height: "320px" }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={mockChartData}>
            <CartesianGrid stroke="#333" strokeDasharray="0" />
            <XAxis
              dataKey="date"
              tick={{ fill: "#666", fontSize: 12 }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              yAxisId="left"
              orientation="left"
              stroke="#666"
              tick={{ fontSize: 12, fill: "#5295E0" }}
            />
            <YAxis
              yAxisId="right"
              orientation="right"
              stroke="#666"
              tick={{ fontSize: 12, fill: "#6B4DBA" }}
            />
            {activeMetrics.map((metricKey, idx) => {
              const metric = metricsMock.find((m) => m.key === metricKey);
              return (
                <Line
                  key={metricKey}
                  type="monotone"
                  dataKey={metricKey}
                  stroke={metric?.color || "#8884d8"}
                  strokeWidth={2}
                  dot={false}
                  yAxisId={idx === 0 ? "left" : "right"}
                />
              );
            })}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};
